/**
 * R7/R8's confirm split, and the `/message/delete` call.
 *
 * The request body is `{ messageId }` in both the own-message and moderator
 * paths, and nothing about which confirm fired is ever sent. That is R9's
 * structural half: a renderer downstream cannot tell which path produced a
 * deletion because the wire never carried the distinction.
 *
 * This hook does not re-check `canDeleteMessage`. The caller gates whether
 * `Delete` is offered at all. If the caller gets that wrong the server still
 * refuses with `NOT_MESSAGE_OWNER` and the shared error modal names it.
 */
import { useMessageDelete } from "@sportsmet/messaging-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  buildModeratorDeleteConfirm,
  buildOwnDeleteConfirm,
} from "./moderationCopy";
import { isFailedSdkResult, sdkErrorCode } from "./moderationRules";

export function useDeleteMessage(): {
  deleteMessage: (payload: {
    messageId: string;
    senderId: string;
    callerId: string;
    senderDisplayName: string;
  }) => Promise<{ didDelete: boolean; errorCode: string | null }>;
  isPending: boolean;
} {
  const messageDelete = useMessageDelete();

  const deleteMessage = async (payload: {
    messageId: string;
    senderId: string;
    callerId: string;
    senderDisplayName: string;
  }): Promise<{ didDelete: boolean; errorCode: string | null }> => {
    const isOwnMessage = payload.senderId === payload.callerId;
    const confirmed = await confirmDestructive(
      isOwnMessage
        ? buildOwnDeleteConfirm()
        : buildModeratorDeleteConfirm({
            senderDisplayName: payload.senderDisplayName,
          }),
    );
    if (!confirmed) {
      return { didDelete: false, errorCode: null };
    }

    try {
      const result = await messageDelete.mutateAsync({
        messageId: payload.messageId,
      });
      if (isFailedSdkResult(result)) {
        showApiError(result);
        return { didDelete: false, errorCode: sdkErrorCode(result) };
      }
      return { didDelete: true, errorCode: null };
    } catch {
      showOfflineError();
      return { didDelete: false, errorCode: null };
    }
  };

  return { deleteMessage, isPending: messageDelete.isPending };
}
